// File: frontend/src/pages/tenantTodayLogic.js
// Version: 1.0 (2026-09-28, Phase 9 — план 09-05)
// Description: чистые функции экрана Today арендатора (TenantToday.js).
//
// 🔴 ЗДЕСЬ НЕТ НИ ОДНОГО ФАКТА ФИРМЫ. Адрес, условия консультации, формат встречи —
//    всё выводится из ответов ручек (/ofisy/, /today/bookings, /today/consultation-formats).
//    Нет данных → null / 'unknown', а не правдоподобное значение по умолчанию.
//
// 🔴 ЧЕТЫРЕ СОСТОЯНИЯ БЛОКА: loading / ready / denied (403) / failed (прочее).
//    Пустота и отказ никогда не рисуются нулём.
//
// 🔴 ФОРМАТ ВСТРЕЧИ [PROPOSED — правило формата]: 'unknown' («неизвестен» — лида нет,
//    id не найден, ответ не загружен/отказ) ≠ 'not_recorded' («не записан» — лид найден,
//    строки «Type: …» в заметках нет). Сливать их нельзя: «не записан» — утверждение
//    о лиде, которое можно сделать ТОЛЬКО прочитав лида.

export const ZAGRUZKA = Object.freeze({ state: 'loading', data: null, reason: null });

const FORMATY = ['in_person', 'phone', 'video'];

/** 403 → 'denied' (не положено); всё прочее → 'failed' (не смогли спросить). */
export const rodOshibki = (err) => (err && err.status === 403 ? 'denied' : 'failed');

const OBRAZEC_LEAD_ID = /^Lead ID: (\d+)$/m;

/** Lead ID из описания события календаря (строка `Lead ID: N`, которую пишет запись). */
export const leadIdIzSobytiya = (sobytie) => {
    const opisanie = sobytie && sobytie.description;
    if (typeof opisanie !== 'string') return null;
    const m = OBRAZEC_LEAD_ID.exec(opisanie);
    return m ? Number(m[1]) : null;
};

/**
 * Формат встречи события по ответу /today/consultation-formats `{formats, unknown_ids}`.
 * Возвращает 'in_person' | 'phone' | 'video' | 'not_recorded' | 'unknown'.
 */
export const formatVstrechi = (sobytie, otvetFormatov) => {
    const id = leadIdIzSobytiya(sobytie);
    if (id === null) return 'unknown';
    if (!otvetFormatov || typeof otvetFormatov !== 'object') return 'unknown';
    const formats = otvetFormatov.formats || {};
    const kluch = String(id);
    if (!Object.prototype.hasOwnProperty.call(formats, kluch)) return 'unknown';
    const f = formats[kluch];
    if (f === null) return 'not_recorded';
    return FORMATY.includes(f) ? f : 'unknown';
};

/** Местные сутки браузера: [полночь, полночь + 24 ч] в ISO. */
export const granitsySutok = (seychas = new Date()) => {
    const s = new Date(seychas.getFullYear(), seychas.getMonth(), seychas.getDate());
    const e = new Date(s.getTime() + 24 * 60 * 60 * 1000);
    return [s.toISOString(), e.toISOString()];
};

/** Ключ приветствия. Пороги те же, что у общего дашборда: 12 и 17. */
export const klyuchPrivetstviya = (chas) => {
    if (chas < 12) return 'today.greeting.morning';
    if (chas < 17) return 'today.greeting.afternoon';
    return 'today.greeting.evening';
};

export const lokal = (lang) => (lang === 'ru' ? 'ru-RU' : 'en-US');

export const vremya = (iso, lang) => {
    const d = new Date(iso);
    if (!iso || Number.isNaN(d.getTime())) return '—';   // ПРОЧЕРК, А НЕ ВЫДУМАННОЕ ВРЕМЯ
    return d.toLocaleTimeString(lokal(lang), { hour: '2-digit', minute: '2-digit' });
};

export const data = (iso, lang) => {
    const d = new Date(iso);
    if (!iso || Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(lokal(lang), { day: 'numeric', month: 'long' });
};

// Дата срока разбирается ПО ЧАСТЯМ, а не через new Date(строка): голая дата
// «ГГГГ-ММ-ДД» читается как полночь UTC и западнее Гринвича съезжает на сутки назад.
export const dataSroka = (iso, lang) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
    if (!m) return null;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
        .toLocaleDateString(lokal(lang), { day: 'numeric', month: 'long', year: 'numeric' });
};

/** Адрес главного офиса для посетителя (adres_naruzhu) из ответа /ofisy/; нет → null. */
export const adresGlavnogoOfisa = (otvet) => {
    const ofisy = otvet && Array.isArray(otvet.ofisy) ? otvet.ofisy : [];
    const glavnyy = ofisy.find((o) => o && o.glavnyy === true);
    return (glavnyy && glavnyy.adres_naruzhu) || null;
};

/**
 * Условия консультации из ответа /today/bookings. Любое из двух полей не задано →
 * 'terms.unknown': «бесплатная» по умолчанию была бы выдумкой.
 */
export const usloviyaKonsultacii = (otvet) => {
    const minutes = otvet ? otvet.consultation_minutes : null;
    const platnaya = otvet ? otvet.consultation_is_paid : null;
    if (typeof minutes !== 'number' || typeof platnaya !== 'boolean') {
        return { kluch: 'terms.unknown', minutes: null };
    }
    return { kluch: platnaya ? 'terms.paid' : 'terms.free', minutes };
};
